import { createFileRoute, useRouter } from '@tanstack/react-router';
import { FileText, Layers, Plus } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { SelectionPlaceholder } from '@/components/molecules';
import { TemplateListPanel } from '@/components/organisms/templates/TemplateListPanel';
import { Route as TemplatesLayoutRoute } from '../templates';
import type { TemplateTypeFilter } from '@/components/organisms/templates/TemplateListPanel';

export const Route = createFileRoute('/_dashboard/dashboard/settings/templates/')({
  component: TemplatesIndexPage,
});

function cleanTemplateSearch(next: Record<string, unknown>) {
  if (next.q == null || next.q === '') delete next.q;
  if (next.type === 'all') delete next.type;
  return next as { type?: TemplateTypeFilter; q?: string };
}

function buildTemplateChildSearch(type: TemplateTypeFilter, q: string) {
  return {
    tab: 'overview' as const,
    ...cleanTemplateSearch({ type, q }),
  };
}

function TemplatesIndexPage() {
  const router = useRouter();
  const navigateTemplates = TemplatesLayoutRoute.useNavigate();
  const filters = TemplatesLayoutRoute.useSearch();
  const typeFilter = filters.type ?? 'all';
  const query = filters.q ?? '';

  function updateFilters(patch: { type?: TemplateTypeFilter; q?: string }) {
    void navigateTemplates({
      search: (prev) => cleanTemplateSearch({ ...prev, ...patch }),
      replace: true,
    });
  }

  return (
    <>
      {/* Desktop: empty state (split/table layout handles display) */}
      <div className="hidden md:flex flex-col items-center justify-center h-full min-h-[300px] text-center px-6 py-12">
        <div className="flex items-center gap-3 mb-4 opacity-20">
          <FileText className="h-10 w-10" />
          <Layers className="h-10 w-10" />
        </div>
        <SelectionPlaceholder
          icon={FileText}
          title="Select a template to edit, or create a new one."
          iconClassName="hidden"
          className="flex flex-col items-center justify-center gap-2 text-muted-foreground"
        />
      </div>

      {/* Mobile: full list */}
      <div className="md:hidden flex flex-col" style={{ height: 'calc(100dvh - 5rem)' }}>
        <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
          <h1 className="text-lg font-semibold">Templates</h1>
          <Button
            size="icon"
            aria-label="Create template"
            onClick={() =>
              void router.navigate({ to: '/dashboard/settings/templates/estimate/new' })
            }
          >
            <Plus size={16} />
          </Button>
        </div>
        <TemplateListPanel
          typeFilter={typeFilter}
          onTypeFilterChange={(type) => updateFilters({ type })}
          search={query}
          onSearchChange={(q) => updateFilters({ q })}
          onSelect={(kind, id) => {
            if (kind === 'estimate') {
              void router.navigate({
                to: '/dashboard/settings/templates/estimate/$id',
                params: { id },
                search: () => buildTemplateChildSearch(typeFilter, query),
              });
            } else {
              void router.navigate({
                to: '/dashboard/settings/templates/proposal/$id',
                params: { id },
                search: () => buildTemplateChildSearch(typeFilter, query),
              });
            }
          }}
        />
      </div>
    </>
  );
}
